<template>
  <app-collapse
    is-open
    :heading="`${heading} (${headingTotal})`"
    inner-class="space-y-3 mx-0.5"
  >
    <hr class="border-t border-gray-200 mt-2" />
    <div class="space-y-2">
      <identifiers-search-field
        size="sm"
        :has-shortcut="false"
        :store="identifierStore"
      />
      <p
        v-if="searchText && identifiers?.data?.length"
        v-html="paginationSummary"
        class="text-xs mx-2"
      />
    </div>
    <template v-if="!identifiers?.data?.length">
      <div class="bg-gray-100 rounded-xl">
        <app-empty-state
          size="sm"
          icon="face-sad"
          container-class="py-20"
          title="No identifiers matched your search"
          :description="`We couldn't find anything matching “${searchText}” in identifiers. Please try again.`"
        />
      </div>
    </template>
    <template v-else>
      <div class="space-y-0.5">
        <checkbox-label
          v-for="(identifier, idx) in linkedIdentifiers"
          :key="idx"
          :value="identifier.id"
          v-model="checkedIds"
          :class="{ 'bg-brand-25 hover:bg-brand-50!': isChecked(identifier) }"
        >
          <identifier-option-label
            :identifier="identifier"
            :is-checked="isChecked(identifier)"
            :target-recipient="targetTransferRecipient"
          />
        </checkbox-label>
        <button
          v-if="isShowMoreLinkedButtonVisible"
          type="button"
          class="btn btn-ghost"
          :disabled="isFetchingNextRecipientIdentifiersPage"
          @click="fetchNextRecipientIdentifiersPage"
        >
          <app-icon :name="isFetchingNextRecipientIdentifiersPage ? 'loading' : 'plus'" />
          {{ isFetchingNextRecipientIdentifiersPage ? 'Loading...' : 'Load more' }}
        </button>
        <hr
          v-if="linkedIdentifiers.length && otherIdentifiers.length"
          class="border-dashed border-gray-300 my-3"
        />
        <checkbox-label
          v-for="(identifier, idx) in otherIdentifiers"
          :key="idx"
          :value="identifier.id"
          v-model="checkedIds"
          :class="{ 'bg-gray-50': isChecked(identifier) }"
        >
          <identifier-option-label
            :identifier="identifier"
            :is-checked="isChecked(identifier)"
            :target-recipient="targetTransferRecipient"
          />
        </checkbox-label>
        <button
          v-if="hasNextPage"
          type="button"
          class="btn btn-ghost"
          :disabled="isFetchingNextPage"
          @click="fetchNextPage"
        >
          <app-icon :name="isFetchingNextPage ? 'loading' : 'plus'" />
          <span class="font-semibold">{{ isFetchingNextPage ? 'Loading...' : 'Load more' }}</span>
        </button>
      </div>
    </template>
  </app-collapse>
</template>

<script setup>
import { computed, ref, toRef, watch } from 'vue';
import { useInfiniteIdentifiersByAgencyId } from '@/services/identifiers/queries.js';
import {
  useInfiniteIdentifiersByTransferRecipientId,
  useTransferRecipientById,
} from '@/services/transfer-recipients/queries.js';
import { useIdentifierStore } from '@/stores/identifiers';
import { useFiltersReset } from '@/composables/useFiltersReset';
import CheckboxLabel from '@/components/form/CheckboxLabel.vue';
import IdentifierOptionLabel from '@/components/identifiers/IdentifierOptionLabel.vue';
import IdentifiersSearchField from '../identifiers/IdentifiersSearchField.vue';

const emit = defineEmits(['update:checkedIds']);

const props = defineProps({
  heading: {
    type: String,
    default: 'Manage identifiers',
  },
  transferRecipientId: {
    type: String,
  },
  transferRecipientPayload: {
    type: Object,
  },
  agencyId: {
    type: String,
    required: true,
  },
});

const isNewTransferRecipient = computed(() => !props.transferRecipientId);
const headingTotal = computed(() => (isNewTransferRecipient.value ? totalIdentifiers.value : checkedIds.value.length));

const identifierStore = useIdentifierStore();
const searchText = computed(() => identifierStore.filters.search_text);

useFiltersReset(identifierStore);

const recipientId = computed(() => props.transferRecipientId);
const { data: existingRecipient } = useTransferRecipientById(recipientId);

const {
  data: identifiersData,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
} = useInfiniteIdentifiersByAgencyId(toRef(props, 'agencyId'));

const identifiers = computed(() => {
  const data = identifiersData.value?.pages?.flatMap((page) => page.data) ?? [];
  const meta = identifiersData.value?.pages?.[0]?.meta;
  return { data, meta };
});

const paginationSummary = computed(() => {
  const loadedCount = identifiers.value?.data?.length ?? 0;
  const total = totalIdentifiers.value;
  return `Showing ${loadedCount} of ${total} results for <span class="text-brand-600">'${searchText.value}'</span>`;
});

const totalIdentifiers = computed(() => identifiers?.value?.meta?.pagination?.total ?? 0);

const targetTransferRecipient = computed(() => {
  if (props.transferRecipientId) {
    return existingRecipient?.value?.data;
  }
  if (props.transferRecipientPayload) {
    return {
      business_name: props.transferRecipientPayload?.beneficiary?.bank_details?.account_name,
      country_code: props.transferRecipientPayload?.beneficiary?.bank_details?.bank_country_code,
      currency_alpha_code: props.transferRecipientPayload?.beneficiary?.bank_details?.account_currency,
    };
  }
  return {};
});

// ----------------------------------
// Identifiers by transfer recipient
// ----------------------------------
const {
  data: recipientIdentifiersData,
  hasNextPage: hasNextRecipientIdentifiersPage,
  isFetchingNextPage: isFetchingNextRecipientIdentifiersPage,
  fetchNextPage: fetchNextRecipientIdentifiersPage,
} = useInfiniteIdentifiersByTransferRecipientId(recipientId);

const identifiersByTransferRecipient = computed(() => {
  const data = recipientIdentifiersData.value?.pages?.flatMap((page) => page.data) ?? [];
  const meta = recipientIdentifiersData.value?.pages?.[0]?.meta;
  return { data, meta };
});

// ----------------
// Grouping
// ----------------
const isAssociated = (id) => {
  return targetTransferRecipient.value?.agency_identifiers?.some((identifier) => identifier.id === id);
};

const linkedIdentifiersByAgencyId = computed(
  () => identifiers.value?.data?.filter((identifier) => isAssociated(identifier.id)) ?? []
);

const linkedIdentifiers = computed(() => {
  if (!searchText.value) {
    // If there is no search value, identifiers may exceed pagination.
    // Using this query's results ensures that any linked identifiers are always available in the UI.
    return identifiersByTransferRecipient.value?.data ?? [];
  } else {
    // If there is a search value, there shouldn't be too many results
    return linkedIdentifiersByAgencyId.value ?? [];
  }
});

const isShowMoreLinkedButtonVisible = computed(() => !searchText.value && hasNextRecipientIdentifiersPage.value);

const otherIdentifiers = computed(() => {
  const ids = identifiers.value?.data?.filter((identifier) => !isAssociated(identifier.id)) ?? [];
  return ids.sort((a, b) => (!a.transfer_party ? -1 : b.transfer_party ? 1 : 0));
});

const unlinkedIdentifiers = computed(
  () => identifiers.value?.data?.filter((identifier) => !identifier.transfer_party) ?? []
);

// ----------------
// Checked State
// ----------------
const checkedIds = ref([]);

const setChecked = (agencyIdentifiers) => {
  checkedIds.value = agencyIdentifiers.map((idf) => idf.id);
};

const isPristine = computed(() => {
  if (checkedIds.value.length !== linkedIdentifiers.value.length) return false;
  const isAllCheckedAssociated = checkedIds.value?.every(isAssociated);
  const isSameLength = checkedIds.value.length === linkedIdentifiers.value.length;
  return isAllCheckedAssociated && isSameLength;
});

const isChecked = (identifier) => {
  return checkedIds.value.includes(identifier.id);
};

watch(checkedIds, (newVal) => {
  emit('update:checkedIds', newVal);
});

watch(
  () => targetTransferRecipient.value,
  (recipient) => {
    if (recipient && recipient.agency_identifiers) {
      setChecked(recipient.agency_identifiers);
    } else if (isNewTransferRecipient.value && isPristine.value) {
      // Set all available identifiers by default for new beneficiary
      setChecked(unlinkedIdentifiers.value);
    }
  },
  { immediate: true }
);

defineExpose({
  isPristine,
});
</script>
